import React, { useState } from 'react';
import {
    Calendar as CalendarIcon,
    Users,
    Bell,
    MapPin,
    Clock,
    Edit,
    Share2,
    Check,
    AlertCircle,
    UserPlus
} from 'lucide-react';

const CalendarDemo = () => {
    // 模擬行事曆數據
    const [calendarData, setCalendarData] = useState({
        companyEvents: [
            {
                id: 1,
                title: '年度策略規劃會議',
                date: '2024-03-15',
                time: '09:00 - 12:00',
                location: '總部會議室',
                participants: ['高階管理層'],
                type: 'meeting'
            },
            {
                id: 2,
                title: '新人培訓課程',
                date: '2024-03-20',
                time: '14:00 - 17:00',
                location: '線上會議',
                participants: ['人資部', '新進員工'],
                type: 'training'
            }
        ],
        personalEvents: [
            {
                id: 1,
                title: '季度績效檢討',
                date: '2024-03-18',
                time: '10:00 - 11:30',
                type: 'performance-review'
            },
            {
                id: 2,
                title: '專案進度會議',
                date: '2024-03-22',
                time: '15:00 - 16:00',
                type: 'project-meeting'
            }
        ],
        upcomingReminders: [
            {
                id: 1,
                title: '提交季度報告',
                date: '2024-03-25',
                time: '全天',
                type: 'deadline'
            },
            {
                id: 2,
                title: '部門團建活動報名',
                date: '2024-03-30',
                time: '截止日期',
                type: 'registration'
            }
        ]
    });

    // 創建新事件
    const [newEvent, setNewEvent] = useState({
        title: '',
        date: '',
        time: '',
        participants: []
    });

    // 處理事件創建
    const handleCreateEvent = () => {
        // 模擬事件創建邏輯
        const updatedPersonalEvents = [
            ...calendarData.personalEvents,
            {
                id: calendarData.personalEvents.length + 1,
                ...newEvent,
                type: 'personal'
            }
        ];

        setCalendarData(prev => ({
            ...prev,
            personalEvents: updatedPersonalEvents
        }));

        // 重置新事件表單
        setNewEvent({
            title: '',
            date: '',
            time: '',
            participants: []
        });
    };

    // 事件類型圖示
    const getEventIcon = (type) => {
        switch (type) {
            case 'meeting':
                return <Users className="text-blue-500" />;
            case 'training':
                return <Bell className="text-green-500" />;
            case 'performance-review':
                return <Check className="text-purple-500" />;
            case 'project-meeting':
                return <Edit className="text-orange-500" />;
            case 'deadline':
                return <Clock className="text-red-500" />;
            case 'registration':
                return <Share2 className="text-teal-500" />;
            default:
                return <CalendarIcon className="text-gray-500" />;
        }
    };

    return (
        <div className="p-8">
            <div className="grid md:grid-cols-2 gap-8">
                {/* 左側：公司行事曆 */}
                <div>
                    <h2 className="text-2xl font-bold mb-4 flex items-center">
                        <CalendarIcon className="mr-2 text-primary-600" />
                        公司行事曆
                    </h2>

                    <div className="space-y-4">
                        {calendarData.companyEvents.map((event) => (
                            <div
                                key={event.id}
                                className="bg-white p-4 rounded-lg shadow-sm flex items-start"
                            >
                                <div className="mr-4 mt-1">
                                    {getEventIcon(event.type)}
                                </div>
                                <div className="flex-grow">
                                    <div className="flex justify-between items-center mb-2">
                                        <h3 className="font-semibold">{event.title}</h3>
                                        <span className="text-sm text-gray-600">{event.date}</span>
                                    </div>
                                    <div className="flex items-center mb-2">
                                        <Clock className="mr-2 text-gray-500" size={16} />
                                        <span className="text-sm">{event.time}</span>
                                    </div>
                                    <div className="flex items-center">
                                        <MapPin className="mr-2 text-gray-500" size={16} />
                                        <span className="text-sm">{event.location}</span>
                                    </div>
                                    <div className="mt-2 flex items-center">
                                        <Users className="mr-2 text-gray-500" size={16} />
                                        <span className="text-sm text-gray-600">
                                            參與者：{event.participants.join('、')}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-6 bg-gray-50 p-4 rounded-lg">
                        <h3 className="font-semibold mb-3 flex items-center">
                            <Bell className="mr-2 text-yellow-500" />
                            公司重要日程提醒
                        </h3>
                        <ul className="space-y-2">
                            {calendarData.upcomingReminders.map((reminder) => (
                                <li
                                    key={reminder.id}
                                    className="flex items-center justify-between bg-white p-3 rounded-lg shadow-sm"
                                >
                                    <div className="flex items-center">
                                        {getEventIcon(reminder.type)}
                                        <span className="ml-3 font-medium">{reminder.title}</span>
                                    </div>
                                    <span className="text-sm text-gray-600">
                                        {reminder.date} {reminder.time}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* 右側：個人行事曆與事件創建 */}
                <div>
                    <h3 className="text-xl font-semibold mb-4 flex items-center">
                        <Edit className="mr-2 text-primary-600" />
                        個人行事曆
                    </h3>

                    <div className="bg-white rounded-lg shadow-md mb-6">
                        <div className="p-4 border-b">
                            <h4 className="font-semibold">我的近期安排</h4>
                        </div>
                        {calendarData.personalEvents.map((event) => (
                            <div
                                key={event.id}
                                className="p-4 border-b flex items-center justify-between hover:bg-gray-50"
                            >
                                <div className="flex items-center">
                                    {getEventIcon(event.type)}
                                    <div className="ml-3">
                                        <div className="font-medium">{event.title}</div>
                                        <div className="text-sm text-gray-600">
                                            {event.date} {event.time}
                                        </div>
                                    </div>
                                </div>
                                <button className="text-primary-600 hover:bg-primary-50 p-2 rounded-full">
                                    <Edit size={16} />
                                </button>
                            </div>
                        ))}
                    </div>

                    <div className="bg-gray-50 p-6 rounded-lg">
                        <h4 className="font-semibold mb-4 flex items-center">
                            <Share2 className="mr-2 text-primary-600" />
                            創建新事件
                        </h4>
                        <div className="space-y-4">
                            <div>
                                <label className="block mb-2 text-sm font-medium">事件標題</label>
                                <input
                                    type="text"
                                    value={newEvent.title}
                                    onChange={(e) => setNewEvent(prev => ({
                                        ...prev,
                                        title: e.target.value
                                    }))}
                                    className="w-full p-2 border rounded-lg"
                                    placeholder="輸入事件標題"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block mb-2 text-sm font-medium">日期</label>
                                    <input
                                        type="date"
                                        value={newEvent.date}
                                        onChange={(e) => setNewEvent(prev => ({
                                            ...prev,
                                            date: e.target.value
                                        }))}
                                        className="w-full p-2 border rounded-lg"
                                    />
                                </div>
                                <div>
                                    <label className="block mb-2 text-sm font-medium">時間</label>
                                    <input
                                        type="time"
                                        value={newEvent.time}
                                        onChange={(e) => setNewEvent(prev => ({
                                            ...prev,
                                            time: e.target.value
                                        }))}
                                        className="w-full p-2 border rounded-lg"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block mb-2 text-sm font-medium">參與者</label>
                                <div className="flex items-center">
                                    <input
                                        type="text"
                                        value={newEvent.participants.join(', ')}
                                        onChange={(e) => setNewEvent(prev => ({
                                            ...prev,
                                            participants: e.target.value.split(',').map(p => p.trim())
                                        }))}
                                        className="flex-grow p-2 border rounded-lg mr-2"
                                        placeholder="輸入參與者姓名，用逗號分隔"
                                    />
                                    <button className="bg-primary-50 text-primary-600 p-2 rounded-full">
                                        <UserPlus size={20} />
                                    </button>
                                </div>
                            </div>
                            <button
                                onClick={handleCreateEvent}
                                disabled={!newEvent.title || !newEvent.date}
                                className="w-full bg-primary-600 text-white p-3 rounded-lg hover:bg-primary-700 disabled:bg-gray-400"
                            >
                                創建事件
                            </button>
                        </div>
                    </div>

                    <div className="mt-6 bg-white p-4 rounded-lg shadow-sm">
                        <h4 className="font-semibold mb-3 flex items-center">
                            <AlertCircle className="mr-2 text-yellow-500" />
                            日程共享與設定
                        </h4>
                        <p className="text-sm text-gray-600 mb-3">
                            您可以與同事或家人共享您的部分日程，控制可見的詳細程度。
                        </p>
                        <div className="flex space-x-4">
                            <button className="flex-1 bg-primary-50 text-primary-600 p-2 rounded-lg">
                                共享設定
                            </button>
                            <button className="flex-1 bg-primary-50 text-primary-600 p-2 rounded-lg">
                                匯入/匯出
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* 底部操作區域 */}
            <div className="mt-8 flex justify-between items-center bg-white p-6 rounded-lg shadow-md">
                <div>
                    <h4 className="font-semibold">日程管理與協作</h4>
                    <p className="text-sm text-gray-600">
                        智能整合個人與公司日程，提升溝通效率和時間管理
                    </p>
                </div>
                <div className="flex space-x-4">
                    <button className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors">
                        日程同步
                    </button>
                    <button className="border border-primary-600 text-primary-600 px-4 py-2 rounded-lg hover:bg-primary-50 transition-colors">
                        協作設定
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CalendarDemo;